import React from 'react';
import { Box, Typography, Button, CircularProgress, Fade } from '@mui/material';
import { Download } from '@mui/icons-material';

interface CompletionFooterProps {
  isCompleted: boolean;
  downloadLoading: boolean;
  stateCleared: boolean;
  completionResult: string;
  // 🆕 Optional multi-file outputs
  outputFiles?: Array<{ label: string; key: string }>;
  // 🆕 Optional bundle download
  showBundleDownload?: boolean;
  error?: string; // 🆕 Add error prop
  onDownload: (fileKey: string) => void;
  onDownloadBundle?: () => void;
  onClose: () => void;
}

export const CompletionFooter: React.FC<CompletionFooterProps> = ({
  isCompleted,
  downloadLoading,
  stateCleared,
  completionResult,
  outputFiles,
  showBundleDownload,
  error,
  onDownload,
  onDownloadBundle,
  onClose,
}) => {
  // Show footer for both completion and error states
  if (!isCompleted && !error) return null;

  // 🎯 DETECT: No text extractable scenarios for better UX
  const isNoTextError = error?.includes('no extractable text') || 
                        error?.includes('contain no extractable text') ||
                        error?.includes('scanned image') ||
                        error?.includes('empty document');

  return (
    <Fade in={isCompleted || !!error} timeout={500}>
      <Box sx={{ 
        p: 3,
        bgcolor: 'rgba(8, 15, 24, 0.82)',
        borderTop: '1px solid rgba(148, 163, 184, 0.18)',
        textAlign: 'center',
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        backdropFilter: 'blur(14px)'
      }}>
        {/* 🎉 SUCCESS STATE */}
        {isCompleted && !error && (
          <>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#f8fafc', 
                fontWeight: 600, 
                mb: 1,
                fontSize: '1.125rem'
              }}
            >
              Processing Complete!
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(226, 232, 240, 0.76)', 
                mb: 2,
                fontSize: '0.875rem'
              }}
            >
              {outputFiles && outputFiles.length > 1 ? 'Your documents are ready for download.' : 'Your document has been successfully processed and is ready for download.'}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {outputFiles && outputFiles.length > 1 ? (
                outputFiles.map((f, idx) => (
                  <Button
                    key={f.key}
                    variant="contained"
                    onClick={() => onDownload(f.key)}
                    disabled={downloadLoading}
                    startIcon={downloadLoading ? <CircularProgress size={18} /> : <Download />}
                    sx={{
                      bgcolor: 'rgba(34, 211, 238, 0.16)',
                      color: '#67e8f9',
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      '&:hover': { bgcolor: 'rgba(34, 211, 238, 0.24)' },
                      '&:disabled': { bgcolor: 'rgba(148, 163, 184, 0.24)', color: 'rgba(226, 232, 240, 0.5)' }
                    }}
                  >
                    {downloadLoading ? 'Downloading...' : `Download ${f.label}`}
                  </Button>
                ))
              ) : (
                <Button
                  variant="contained"
                  onClick={() => onDownload(completionResult)}
                  disabled={downloadLoading}
                  startIcon={downloadLoading ? <CircularProgress size={18} /> : <Download />}
                  sx={{
                    bgcolor: 'rgba(34, 211, 238, 0.16)',
                    color: '#67e8f9',
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { 
                      bgcolor: 'rgba(34, 211, 238, 0.24)'
                    },
                    '&:disabled': {
                      bgcolor: 'rgba(148, 163, 184, 0.24)',
                      color: 'rgba(226, 232, 240, 0.5)',
                    }
                  }}
                >
                  {downloadLoading ? 'Downloading...' : 'Download Document'}
                </Button>
              )}
              {showBundleDownload && (
                <Button
                  variant="outlined"
                  onClick={onDownloadBundle}
                  sx={{
                    borderColor: 'rgba(148, 163, 184, 0.26)',
                    color: '#e2e8f0',
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { borderColor: 'rgba(148, 163, 184, 0.4)', bgcolor: 'rgba(255, 255, 255, 0.04)' }
                  }}
                >
                  Download All
                </Button>
              )}
              
              <Button
                variant="outlined"
                sx={{
                  borderColor: 'rgba(148, 163, 184, 0.26)',
                  color: 'rgba(226, 232, 240, 0.76)',
                  fontWeight: 400,
                  px: 3,
                  py: 1,
                  borderRadius: 1,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  '&:hover': { 
                    borderColor: 'rgba(148, 163, 184, 0.4)',
                    bgcolor: 'rgba(255, 255, 255, 0.04)'
                  }
                }}
                onClick={onClose}
              >
                Close
              </Button>
            </Box>
          </>
        )}

        {/* 🚨 ERROR STATE */}
        {error && (
          <>
            <Typography 
              variant="h6" 
              sx={{ 
                color: isNoTextError ? '#0c4a6e' : '#dc2626', 
                fontWeight: 600, 
                mb: 1,
                fontSize: '1.125rem'
              }}
            >
              {isNoTextError ? 'No Text Content Found' : 'Processing Failed'}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(226, 232, 240, 0.76)', 
                mb: 2,
                fontSize: '0.875rem'
              }}
            >
              {isNoTextError 
                ? 'Please try uploading documents with readable text content.'
                : 'An error occurred during processing. Please try again or contact support.'}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  bgcolor: isNoTextError ? '#0ea5e9' : '#dc2626',
                  color: 'white',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: 1,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  '&:hover': { 
                    bgcolor: isNoTextError ? '#0284c7' : '#b91c1c'
                  }
                }}
              >
                {isNoTextError ? 'Upload Different Files' : 'Try Again'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  borderColor: 'rgba(148, 163, 184, 0.26)',
                  color: 'rgba(226, 232, 240, 0.76)',
                  fontWeight: 400,
                  px: 3,
                  py: 1,
                  borderRadius: 1,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  '&:hover': { 
                    borderColor: 'rgba(148, 163, 184, 0.4)',
                    bgcolor: 'rgba(255, 255, 255, 0.04)'
                  }
                }}
              >
                Close
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Fade>
  );
}; 